import { useEffect, useRef, useState } from "react";
import api, { extractErr, mediaUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Newspaper, Plus, Sparkles, Loader2, Save, Trash2, Upload, ExternalLink, Bot } from "lucide-react";

const BLANK = {
  title: "", slug: "", excerpt: "", body_html: "", cover_image: "", cover_alt: "",
  category: "Guides", tags: "", author: "Neel Varma", status: "draft",
  focus_keyword: "", meta_title: "", meta_description: "", meta_keywords: "", faq: [],
};

function toForm(p) {
  if (!p) return { ...BLANK };
  return {
    ...BLANK,
    ...p,
    tags: Array.isArray(p.tags) ? p.tags.join(", ") : (p.tags || ""),
    faq: p.faq || [],
  };
}

export default function SuperBlog() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(BLANK);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [gen, setGen] = useState({ topic: "", keywords: "", notes: "", publish: false });
  const [genBusy, setGenBusy] = useState(false);
  const [optBusy, setOptBusy] = useState(false);
  const [claudeOn, setClaudeOn] = useState(false);
  const [pilot, setPilot] = useState(null);
  const [pilotBusy, setPilotBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/platform/blog/posts").then(({ data }) => setItems(data.items || [])).catch((e) => toast.error(extractErr(e)));
  const loadPilot = () => api.get("/platform/blog/autopilot").then(({ data }) => setPilot(data)).catch(() => {});
  useEffect(() => {
    load();
    loadPilot();
    api.get("/platform/blog/config").then(({ data }) => setClaudeOn(Boolean(data?.enabled))).catch(() => {});
  }, []);

  const savePilot = async (patch) => {
    try {
      const { data } = await api.put("/platform/blog/autopilot", patch);
      setPilot(data);
      toast.success("Autopilot saved");
    } catch (e) { toast.error(extractErr(e)); }
  };

  const runPilot = async () => {
    setPilotBusy(true);
    try {
      const { data } = await api.post("/platform/blog/autopilot/run");
      if (data?.skipped) toast.message(data.error || "Already posted within 48 hours");
      else toast.success(data.title ? `Published: ${data.title}` : "Autopilot ran");
      load();
      loadPilot();
    } catch (e) { toast.error(extractErr(e)); } finally { setPilotBusy(false); }
  };

  const payload = () => ({
    ...form,
    tags: String(form.tags || "").split(",").map((t) => t.trim()).filter(Boolean),
    faq: form.faq || [],
  });

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      if (editingId) {
        const { data } = await api.put(`/platform/blog/posts/${editingId}`, payload());
        toast.success("Post saved");
        setForm(toForm(data));
      } else {
        const { data } = await api.post("/platform/blog/posts", payload());
        toast.success("Post created");
        setEditingId(data.id);
        setForm(toForm(data));
      }
      load();
    } catch (e) { toast.error(extractErr(e)); } finally { setSaving(false); }
  };

  const generate = async () => {
    if (!gen.topic.trim()) { toast.error("Give the AI a topic"); return; }
    setGenBusy(true);
    try {
      const { data } = await api.post("/platform/blog/generate", gen);
      toast.success(data.status === "published" ? "Published" : "Draft created — review before publishing");
      setEditingId(data.id);
      setForm(toForm(data));
      load();
    } catch (e) { toast.error(extractErr(e)); } finally { setGenBusy(false); }
  };

  const optimize = async () => {
    if (!editingId) { toast.error("Save the post first"); return; }
    setOptBusy(true);
    try {
      const { data } = await api.post(`/platform/blog/posts/${editingId}/optimize-seo`);
      setForm(toForm(data.post));
      if (data.notes) toast.message(data.notes);
      else toast.success("SEO pack applied");
    } catch (e) { toast.error(extractErr(e)); } finally { setOptBusy(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete “${p.title}”?`)) return;
    try {
      await api.delete(`/platform/blog/posts/${p.id}`);
      if (editingId === p.id) { setEditingId(null); setForm(BLANK); }
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(extractErr(e)); }
  };

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/platform/blog/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, cover_image: data.url }));
      toast.success("Cover uploaded");
    } catch (e) { toast.error(extractErr(e)); }
  };

  const faq = form.faq || [];
  const inputCls = "bg-slate-800 border-slate-700 text-white";

  return (
    <div className="space-y-6 max-w-5xl pb-16">
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-100 flex items-center gap-2"><Newspaper className="w-7 h-7 text-orange-300" /> Blog</h1>
        <p className="text-slate-400 text-sm mt-1">Publish ranking-focused guides on /blog. Each post needs a unique title and angle — near-duplicates (same festival headline with -1/-2 slugs) will not rank. Autopilot drafts product-only posts using live plan prices. City pages live at /cities/… — never under a merchant store slug.</p>
      </div>

      {pilot && (
        <Card className="bg-slate-900 border-violet-900/50 p-5 space-y-3 text-slate-100" data-testid="blog-autopilot">
          <div className="flex items-center gap-2 text-violet-300"><Bot className="w-4 h-4" /><span className="text-sm font-semibold">AI autopilot</span></div>
          <p className="text-xs text-slate-400">Writes at most one unique post every 48 hours (AI cover included). Skips a festival if that occasion was already covered this year. Default is draft — tick publish only after you review. Brand: Mera Startup · founder Neel Varma. Never Grocode.</p>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid="blog-autopilot-enable" checked={Boolean(pilot.enabled)} onChange={(e) => savePilot({ enabled: e.target.checked })} />
            Enable autopilot (one unique post every 48 hours)
          </label>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Interval</Label>
              <p className="mt-2 text-sm text-slate-300 font-mono-nums" data-testid="blog-autopilot-interval">48 hours · 1 post</p>
            </div>
            <div className="flex flex-col justify-end gap-2 text-sm text-slate-300 pb-1">
              <label className="flex items-center gap-2"><input type="checkbox" checked={Boolean(pilot.auto_publish)} onChange={(e) => savePilot({ auto_publish: e.target.checked })} /> Publish immediately (uncheck = drafts)</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={Boolean(pilot.clickbait)} onChange={(e) => savePilot({ clickbait: e.target.checked })} /> Clickbait headlines (honest claims)</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={Boolean(pilot.festival_posts)} onChange={(e) => savePilot({ festival_posts: e.target.checked })} /> Festival / occasion posts</label>
            </div>
          </div>
          <div className="text-[11px] text-slate-500 font-mono-nums">
            Last run: {pilot.last_run_at || "never"} {pilot.last_slug ? `· ${pilot.last_slug}` : ""} {pilot.posts_written ? `· ${pilot.posts_written} written` : ""}
            {pilot.last_error ? <div className="text-red-400 mt-1">{pilot.last_error}</div> : null}
          </div>
          <Button type="button" variant="outline" className="border-violet-700 text-violet-200" disabled={pilotBusy || !claudeOn} onClick={runPilot} data-testid="blog-autopilot-run">
            {pilotBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />} Run once now
          </Button>
        </Card>
      )}

      <Card className="bg-slate-900 border-slate-800 p-5 space-y-3 text-slate-100">
        <div className="flex items-center gap-2 text-violet-300"><Sparkles className="w-4 h-4" /><span className="text-sm font-semibold">Write with AI</span></div>
        <p className="text-xs text-slate-500">Use a new topic each time (not another “Ganesh Chaturthi Sale Secret…”). Similar titles are rejected so Google does not see duplicate thin posts.</p>
        <Input className={inputCls} placeholder="Unique topic, e.g. GST invoice setup for a new online store in India" value={gen.topic} onChange={(e) => setGen({ ...gen, topic: e.target.value })} data-testid="blog-ai-topic" />
        <div className="grid sm:grid-cols-2 gap-3">
          <Input className={inputCls} placeholder="Keywords (optional)" value={gen.keywords} onChange={(e) => setGen({ ...gen, keywords: e.target.value })} />
          <Input className={inputCls} placeholder="Editor notes (optional)" value={gen.notes} onChange={(e) => setGen({ ...gen, notes: e.target.value })} />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-300">
          <input type="checkbox" checked={gen.publish} onChange={(e) => setGen({ ...gen, publish: e.target.checked })} /> Publish immediately (otherwise save as draft)
        </label>
        <Button onClick={generate} disabled={genBusy || !claudeOn} data-testid="blog-ai-generate" className="bg-violet-600 hover:bg-violet-500">
          {genBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
          {claudeOn ? "Generate post + cover" : "Add OpenRouter API key in Integrations"}
        </Button>
      </Card>

      <div className="grid lg:grid-cols-5 gap-6">
        <Card className="lg:col-span-2 bg-slate-900 border-slate-800 p-0 overflow-hidden text-slate-100">
          <div className="p-3 border-b border-slate-800 flex justify-between items-center">
            <span className="text-sm font-semibold">Posts</span>
            <Button size="sm" variant="outline" className="border-slate-700" onClick={() => { setEditingId(null); setForm(BLANK); }}><Plus className="w-4 h-4 mr-1" /> New</Button>
          </div>
          <div className="divide-y divide-slate-800 max-h-[70vh] overflow-y-auto">
            {items.length === 0 ? <div className="p-6 text-sm text-slate-500">No posts yet.</div> : items.map((p) => (
              <button key={p.id} type="button" onClick={() => { setEditingId(p.id); setForm(toForm(p)); }} className={`w-full text-left p-3 ${editingId === p.id ? "bg-indigo-500/10" : "hover:bg-white/5"}`} data-testid={`blog-row-${p.slug}`}>
                <div className="font-semibold text-sm truncate">{p.title}</div>
                <div className="text-[11px] text-slate-500 font-mono-nums">/{p.slug} · {p.status}</div>
              </button>
            ))}
          </div>
        </Card>

        <Card className="lg:col-span-3 bg-slate-900 border-slate-800 p-5 space-y-3 text-slate-100">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Label>Title</Label><Input className={`mt-1 ${inputCls}`} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="blog-title" /></div>
            <div><Label>Slug</Label><Input className={`mt-1 ${inputCls} font-mono-nums`} value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} /></div>
            <div>
              <Label>Status</Label>
              <select className={`mt-1 w-full h-10 rounded-md px-3 ${inputCls}`} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <option value="draft">draft</option>
                <option value="published">published</option>
              </select>
            </div>
            <div><Label>Category</Label><Input className={`mt-1 ${inputCls}`} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
            <div><Label>Tags</Label><Input className={`mt-1 ${inputCls}`} value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="comma,separated" /></div>
            <div className="sm:col-span-2"><Label>Excerpt</Label><Textarea rows={2} className={`mt-1 ${inputCls}`} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>Body HTML</Label><Textarea rows={12} className={`mt-1 ${inputCls} font-mono text-xs`} value={form.body_html} onChange={(e) => setForm({ ...form, body_html: e.target.value })} data-testid="blog-body" /></div>
            <div><Label>Meta title</Label><Input className={`mt-1 ${inputCls}`} value={form.meta_title} onChange={(e) => setForm({ ...form, meta_title: e.target.value })} /></div>
            <div><Label>Focus keyword</Label><Input className={`mt-1 ${inputCls}`} value={form.focus_keyword} onChange={(e) => setForm({ ...form, focus_keyword: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>Meta description</Label><Textarea rows={2} className={`mt-1 ${inputCls}`} value={form.meta_description} onChange={(e) => setForm({ ...form, meta_description: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>Meta keywords</Label><Input className={`mt-1 ${inputCls}`} value={form.meta_keywords} onChange={(e) => setForm({ ...form, meta_keywords: e.target.value })} /></div>
            <div className="sm:col-span-2">
              <Label>Cover image</Label>
              <div className="flex items-center gap-3 mt-1">
                <div className="w-24 h-14 rounded bg-slate-800 overflow-hidden">{form.cover_image ? <img src={mediaUrl(form.cover_image)} alt="" className="w-full h-full object-cover" /> : null}</div>
                <input ref={fileRef} type="file" hidden accept="image/*" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
                <Button type="button" variant="outline" className="border-slate-700" onClick={() => fileRef.current?.click()}><Upload className="w-4 h-4 mr-1" /> Upload</Button>
              </div>
              <Input className={`mt-2 ${inputCls} text-xs`} value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} placeholder="or paste URL" />
              <Input className={`mt-2 ${inputCls}`} value={form.cover_alt} onChange={(e) => setForm({ ...form, cover_alt: e.target.value })} placeholder="Cover alt text" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>FAQ schema</Label>
            {(faq || []).map((item, i) => (
              <div key={i} className="grid sm:grid-cols-2 gap-2">
                <Input className={inputCls} placeholder="Question" value={item.q || ""} onChange={(e) => setForm({ ...form, faq: faq.map((x, j) => j === i ? { ...x, q: e.target.value } : x) })} />
                <Input className={inputCls} placeholder="Answer" value={item.a || ""} onChange={(e) => setForm({ ...form, faq: faq.map((x, j) => j === i ? { ...x, a: e.target.value } : x) })} />
              </div>
            ))}
            <Button type="button" size="sm" variant="outline" className="border-slate-700" onClick={() => setForm({ ...form, faq: [...faq, { q: "", a: "" }] })}>Add FAQ</Button>
          </div>
          <div className="flex flex-wrap gap-2 pt-2">
            <Button onClick={save} disabled={saving} data-testid="blog-save" className="bg-indigo-600 hover:bg-indigo-500">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save</Button>
            <Button type="button" variant="outline" className="border-violet-800 text-violet-200" disabled={optBusy || !editingId || !claudeOn} onClick={optimize}>
              {optBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />} Optimize SEO
            </Button>
            {form.slug && form.status === "published" && (
              <a href={`/blog/${form.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center text-sm text-indigo-300 px-2"><ExternalLink className="w-4 h-4 mr-1" /> View</a>
            )}
            {editingId && <Button type="button" variant="outline" className="border-red-800 text-red-300 ml-auto" onClick={() => remove({ id: editingId, title: form.title })}><Trash2 className="w-4 h-4" /></Button>}
          </div>
        </Card>
      </div>
    </div>
  );
}
